import React, { useState, useEffect, useRef } from 'react'
import { X, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'react-toastify'
import customerMasterService from '../../services/customerMasterService'
import prospectMasterService from '../../services/prospectMasterService'
import './CustomerSelectionModal.css'

const CustomerSelectionModal = ({ isOpen, onClose, onSelectCustomer, selectedCustomerCode = null, includeProspects = false }) => {
  const [customerData, setCustomerData] = useState([])
  const [prospectData, setProspectData] = useState([])
  const [combinedData, setCombinedData] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  // Use a ref to track if user has manually selected to avoid interference
  const userHasSelectedRef = useRef(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const itemsPerPage = 10

  // Load customer and prospect data
  useEffect(() => {
    if (isOpen) {
      // Reset to page 1 when search term changes
      if (searchTerm !== '') {
        setCurrentPage(1)
      }
      // Debounce search
      const timeoutId = setTimeout(() => {
        loadData()
      }, 300)
      
      return () => clearTimeout(timeoutId)
    }
  }, [isOpen, currentPage, searchTerm, includeProspects])

  // Reset selection only when modal first opens
  useEffect(() => {
    if (isOpen) {
      // Reset selection when modal opens
      setSelectedCustomer(null)
      userHasSelectedRef.current = false
    }
  }, [isOpen])
  
  // Handle pre-selection when combinedData is loaded (only if selectedCustomerCode is provided)
  // This should NOT interfere with manual selection
  useEffect(() => {
    if (isOpen && selectedCustomerCode && combinedData.length > 0 && !userHasSelectedRef.current) {
      const preSelected = combinedData.find(item => 
        item.customerCode === selectedCustomerCode || item.prospectCode === selectedCustomerCode
      )
      if (preSelected) {
        setSelectedCustomer(preSelected)
      }
    }
  }, [combinedData, isOpen, selectedCustomerCode])

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey)
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load customers
      const customerResponse = await customerMasterService.getAllCustomers({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm
      })

      let customers = []
      if (customerResponse && customerResponse.success) {
        customers = (customerResponse.data || []).map(customer => ({
          ...customer,
          type: 'Customer',
          code: customer.customerCode,
          name: customer.customerName,
          category: customer.customerCategory,
          gstin: customer.GSTIN,
          paymentTerms: customer.customerPaymentTerms,
          _id: customer._id || customer.id
        }))
      }

      // Load prospects if includeProspects is true
      let prospects = []
      if (includeProspects) {
        try {
          const prospectResponse = await prospectMasterService.getAllProspects({
            page: currentPage,
            limit: itemsPerPage,
            search: searchTerm
            // Don't filter by status - show all active prospects
          })

          if (prospectResponse && prospectResponse.success) {
            // Backend returns prospects nested in data.prospects
            const prospectsList = prospectResponse.data?.prospects || prospectResponse.data?.data?.prospects || prospectResponse.data || []
            prospects = (Array.isArray(prospectsList) ? prospectsList : []).map(prospect => ({
              ...prospect,
              type: 'Prospect',
              code: prospect.prospectCode,
              name: prospect.prospectName,
              category: prospect.prospectCategory,
              gstin: prospect.GSTIN,
              paymentTerms: prospect.prospectPaymentTerms || 'N/A',
              _id: prospect._id || prospect.id,
              // Map prospect fields to customer-like structure for compatibility
              customerCode: prospect.prospectCode,
              customerName: prospect.prospectName,
              customerCategory: prospect.prospectCategory,
              GSTIN: prospect.GSTIN,
              customerPaymentTerms: prospect.prospectPaymentTerms || 'N/A'
            }))
          }
        } catch (prospectError) {
          console.error('Error loading prospects:', prospectError)
          // Don't show error toast for prospects if it's optional
        }
      }

      // Combine and sort data
      const combined = [...customers, ...prospects].sort((a, b) => {
        // Sort by type first (Customers first), then by name
        if (a.type !== b.type) {
          return a.type === 'Customer' ? -1 : 1
        }
        return (a.name || '').localeCompare(b.name || '')
      })

      setCustomerData(customers)
      setProspectData(prospects)
      setCombinedData(combined)
      
      // Calculate total pages and records
      const totalItems = customers.length + prospects.length
      setTotalPages(Math.ceil(totalItems / itemsPerPage) || 1)
      setTotalRecords(totalItems)
      
    } catch (error) {
      console.error('Error loading data:', error)
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        onClose()
        window.location.href = '/login'
        return
      }
      
      // Handle other errors
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access this data.')
      } else if (error.response?.status === 404) {
        toast.error('Service not found.')
      } else {
        toast.error('Failed to load data. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setCurrentPage(1)
    loadData()
  }

  const handleCustomerSelect = (item) => {
    if (!item) {
      return
    }
    
    userHasSelectedRef.current = true
    setSelectedCustomer(item)
  }

  const handleSaveAndClose = () => {
    if (selectedCustomer) {
      onSelectCustomer(selectedCustomer)
      onClose()
    } else {
      toast.error('Please select a customer before proceeding')
      // Don't close the modal if no customer is selected
      return
    }
  }

  // Removed drag functionality for slide-over drawer

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  if (!isOpen) return null

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div className={`customer-drawer-overlay ${isOpen ? 'open' : ''}`} onClick={handleOverlayClick}>
      <div className={`customer-drawer ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="customer-drawer-header">
          <div className="customer-drawer-header-content">
            <h2>{includeProspects ? 'Customer & Prospect Master' : 'Customer Master'}</h2>
            <p className="customer-drawer-subtitle">
              {includeProspects 
                ? 'Select a customer or prospect from the list' 
                : 'Select a customer from the list'}
            </p>
          </div>
          <button className="customer-drawer-close" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        {/* Search and Pagination */}
        <div className="customer-drawer-search-section">
          <form onSubmit={handleSearch} className="customer-search-form">
            <div className="customer-search-wrapper">
              <Search size={18} className="customer-search-icon" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="customer-search-input"
                placeholder={includeProspects 
                  ? "Search customers or prospects by name, code, or GSTIN..." 
                  : "Search customers by name, code, or GSTIN..."}
              />
            </div>
          </form>
          
          <div className="customer-pagination">
            <button
              className="customer-pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="customer-pagination-info">
              Page {currentPage} of {totalPages} ({totalRecords} total)
            </span>
            <button
              className="customer-pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="customer-drawer-content">
          {loading ? (
            <div className="customer-loading">
              <div className="customer-loading-spinner">Loading...</div>
            </div>
          ) : combinedData.length === 0 ? (
            <div className="customer-empty-state">
              <div className="empty-icon">👥</div>
              <h3>No records found</h3>
              <p>{includeProspects 
                ? 'No active customer or prospect records available.' 
                : 'No active customer master records available.'}
              </p>
            </div>
          ) : (
            <div className="customer-table-container">
              <table className="customer-table">
                <thead>
                  <tr>
                    {includeProspects && <th>Type</th>}
                    <th>{includeProspects ? 'Code' : 'Customer Code'}</th>
                    <th>{includeProspects ? 'Name' : 'Customer Name'}</th>
                    <th>Category</th>
                    <th>GSTIN</th>
                    <th>Payment Terms</th>
                    <th>Select</th>
                  </tr>
                </thead>
                <tbody>
                  {combinedData.map((item) => {
                    const isSelected = selectedCustomer?._id === item._id || 
                      (selectedCustomer?.customerCode === item.customerCode) ||
                      (selectedCustomer?.prospectCode === item.prospectCode)
                    return (
                      <tr 
                        key={item._id || item.id}
                        className={isSelected ? 'selected' : ''}
                        onClick={() => handleCustomerSelect(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        {includeProspects && (
                          <td className="customer-type-cell">
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: item.type === 'Customer' ? '#dbeafe' : '#fef3c7',
                              color: item.type === 'Customer' ? '#1e40af' : '#92400e'
                            }}>
                              {item.type}
                            </span>
                          </td>
                        )}
                        <td className="customer-code-cell">{item.customerCode || item.prospectCode}</td>
                        <td className="customer-name-cell">{item.customerName || item.prospectName}</td>
                        <td className="customer-category-cell">{item.customerCategory || item.prospectCategory}</td>
                        <td className="customer-gstin-cell">{item.GSTIN || item.gstin || 'N/A'}</td>
                        <td className="customer-payment-terms-cell">{item.customerPaymentTerms || item.paymentTerms || 'N/A'}</td>
                        <td className="customer-select-cell" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="radio"
                            name="selectedCustomer"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCustomerSelect(item);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCustomerSelect(item);
                            }}
                            className="customer-radio"
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              
              {combinedData.length === 0 && (
                <div className="customer-no-data">
                  {includeProspects 
                    ? 'No customer or prospect records found.' 
                    : 'No customer master records found.'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="customer-drawer-footer">
          <button className="customer-btn-secondary" onClick={onClose}>
            <ChevronLeft size={18} />
            Cancel
          </button>
          <button 
            className="customer-btn-primary"
            onClick={handleSaveAndClose}
          >
            {includeProspects ? 'Select Customer/Prospect' : 'Select Customer'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomerSelectionModal
